import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../services/supabase'
import { FaSearch, FaTimes } from 'react-icons/fa'
import SchemaMarkup from './SchemaMarkup'

const SearchBar = ({
  type = 'both',
  onCodeSelect = null,
  onConversion = null,
  onFindCorrespondence = null,
  hideConvertButton = false,
  hideCorrespondenceButton = false,
  error: externalError = null,
  setError: setExternalError = null,
  setIsLoading: setExternalIsLoading = null,
  resetSearch = false,
  onSearchReset = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedCode, setSelectedCode] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showResults, setShowResults] = useState(false)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  // Use external error state if provided, otherwise use local state
  const errorState = setExternalError ? externalError : error
  const setErrorState = setExternalError || setError
  const setIsLoadingState = setExternalIsLoading || setIsLoading

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Handle reset search from parent component
  useEffect(() => {
    if (resetSearch) {
      handleClearSearch()
      if (onSearchReset) {
        onSearchReset()
      }
    }
  }, [resetSearch, onSearchReset])

  // Debounce search to avoid too many requests
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      setSearchResults([])
      setShowResults(false)
      return
    }

    const timer = setTimeout(() => {
      searchCodes(searchTerm)
    }, 200) // Reduced debounce time for faster search

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const searchCodes = async (term) => {
    setIsLoading(true)
    setErrorState(null)
    try {
      let iaeResults = []
      let cnaeResults = []
      let cnae2009Results = []

      // Search for IAE codes if type is 'both' or 'iae'
      if (type === 'both' || type === 'iae') {
        const { data: iaeData, error: iaeError } = await supabase.rpc(
          'search_iae_by_code_title',
          {
            input_code: term,
            input_search_term: term,
          }
        )

        if (iaeError) throw iaeError
        if (iaeData) {
          iaeResults = iaeData.map((item) => ({
            ...item,
            type: 'iae',
            code: item.cod_iae,
            title: item.titulo_iae,
            slug: item.slug_iae || item.cod_iae.replace(/\./g, '-'),
            normalizedCode: item.cod_iae.replace(/\./g, ''),
          }))
        }
      }

      // Search for CNAE codes if type is 'both', 'cnae', or 'both-cnae'
      if (type === 'both' || type === 'cnae' || type === 'both-cnae') {
        const { data: cnaeData, error: cnaeError } = await supabase.rpc(
          'search_cnae_2025_by_code_title',
          {
            input_code: term,
            input_search_term: term,
          }
        )

        if (cnaeError) throw cnaeError
        if (cnaeData) {
          cnaeResults = cnaeData.map((item) => ({
            ...item,
            type: 'cnae',
            code: item.cod_cnae,
            title: item.titulo_cnae,
            slug: item.slug_cnae || item.cod_cnae,
            normalizedCode: item.cod_cnae.replace(/\./g, ''),
          }))
        }
      }

      // Search for CNAE 2009 codes if type is 'cnae2009' or 'both-cnae'
      if (type === 'cnae2009' || type === 'both-cnae') {
        const { data: cnae2009Data, error: cnae2009Error } = await supabase.rpc(
          'search_cnae_2009_by_code_title',
          {
            input_code: term,
            input_search_term: term,
          }
        )

        if (cnae2009Error) throw cnae2009Error
        if (cnae2009Data) {
          cnae2009Results = cnae2009Data.map((item) => ({
            ...item,
            type: 'cnae2009',
            code: item.cod_cnae_2009,
            title: item.titulo_cnae_2009,
            slug: item.slug_cnae_2009 || item.cod_cnae_2009,
            normalizedCode: item.cod_cnae_2009.replace(/\./g, ''),
          }))
        }
      }

      // Intercalate results
      let results = []
      const maxLength = Math.max(
        iaeResults.length,
        cnaeResults.length,
        cnae2009Results.length
      )

      for (let i = 0; i < maxLength; i++) {
        if (i < iaeResults.length) results.push(iaeResults[i])
        if (i < cnaeResults.length) results.push(cnaeResults[i])
        if (
          (type === 'cnae2009' || type === 'both-cnae') &&
          i < cnae2009Results.length
        )
          results.push(cnae2009Results[i])
      }

      // For Home.js, only show CNAE 2025 and IAE results, not CNAE 2009
      if (type === 'both') {
        results = results.filter((item) => item.type !== 'cnae2009')
      }

      // For CorrespondenceCnaePage.js, only show CNAE 2025 and CNAE 2009 results, not IAE
      if (type === 'both-cnae') {
        results = results.filter(
          (item) => item.type === 'cnae' || item.type === 'cnae2009'
        )
      }

      // Remove duplicates
      results = results.filter(
        (item, index, self) =>
          index ===
          self.findIndex((t) => t.code === item.code && t.type === item.type)
      )

      setSearchResults(results)
      setShowResults(results.length > 0)
    } catch (error) {
      console.error('Error searching codes:', error)
      setErrorState('Error al buscar códigos. Por favor, inténtalo de nuevo.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value)
    setSelectedCode(null)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSelectedCode(null)
    setSearchResults([])
    setShowResults(false)
  }

  const handleCodeSelect = async (code) => {
    setSelectedCode(code)
    setSearchTerm(code.code + ' - ' + code.title)
    setShowResults(false)

    // Increment search counter when a code is selected
    try {
      let codeType
      if (code.type === 'iae') {
        codeType = 'IAE'
      } else if (code.type === 'cnae') {
        codeType = 'CNAE 2025'
      } else if (code.type === 'cnae2009') {
        codeType = 'CNAE 2009'
      }

      await supabase.rpc('increment_search_count', {
        input_code: code.code,
        input_type: codeType,
      })
    } catch (error) {
      console.error('Error incrementing search counter:', error)
      // Don't show error to user as this is not critical
    }

    if (onCodeSelect) {
      onCodeSelect(code)
    }
  }

  const handleConvert = async () => {
    if (!selectedCode) return

    try {
      setIsLoadingState(true)
      setErrorState(null)

      let associatedCodesList = []
      let associatedCodesDetails = []

      if (selectedCode.type === 'iae') {
        // Get CNAE codes associated with this IAE code
        const { data: associatedCodes, error: associatedError } =
          await supabase.rpc('get_cnae_associated_to_iae_by_code', {
            iae_code: selectedCode.code,
          })

        if (associatedError) throw associatedError
        if (!associatedCodes || associatedCodes.length === 0) {
          setErrorState(
            'No se encontraron códigos CNAE asociados a este código IAE.'
          )
          setIsLoadingState(false)
          return
        }

        associatedCodesList = associatedCodes

        // Get details for each associated CNAE code
        for (const cnaeCode of associatedCodesList) {
          const { data: cnaeDetails, error: cnaeError } = await supabase.rpc(
            'get_cnae_2025_overview_by_code',
            { cnae_code: cnaeCode }
          )

          if (cnaeError) throw cnaeError
          if (cnaeDetails && cnaeDetails.length > 0) {
            associatedCodesDetails.push({
              code: cnaeDetails[0].cod_cnae,
              title: cnaeDetails[0].titulo_cnae,
              description: cnaeDetails[0].descripcion_cnae,
              category: cnaeDetails[0].categoria_cnae,
              level: cnaeDetails[0].nivel_cnae,
              slug: cnaeDetails[0].slug_cnae,
            })
          }
        }
      } else if (selectedCode.type === 'cnae') {
        // Get IAE codes associated with this CNAE code
        const { data: associatedCodes, error: associatedError } =
          await supabase.rpc('get_iae_associated_to_cnae_by_code', {
            cnae_code: selectedCode.code,
          })

        if (associatedError) throw associatedError
        if (!associatedCodes || associatedCodes.length === 0) {
          setErrorState(
            'No se encontraron códigos IAE asociados a este código CNAE.'
          )
          setIsLoadingState(false)
          return
        }

        associatedCodesList = associatedCodes

        // Get details for each associated IAE code
        for (const iaeCode of associatedCodesList) {
          const { data: iaeDetails, error: iaeError } = await supabase.rpc(
            'get_iae_overview_by_code',
            { iae_code: iaeCode }
          )

          if (iaeError) throw iaeError
          if (iaeDetails && iaeDetails.length > 0) {
            associatedCodesDetails.push({
              code: iaeDetails[0].cod_iae,
              title: iaeDetails[0].titulo_iae,
              description: iaeDetails[0].descripcion_iae,
              category: iaeDetails[0].categoria_iae,
              level: iaeDetails[0].nivel_iae,
              slug: iaeDetails[0].slug_iae,
            })
          }
        }
      } else if (selectedCode.type === 'cnae2009') {
        // Get IAE codes associated with this CNAE 2009 code
        const { data: associatedCodes, error: associatedError } =
          await supabase.rpc('get_iae_associated_to_cnae_by_code', {
            cnae_code: selectedCode.code,
          })

        if (associatedError) throw associatedError
        if (!associatedCodes || associatedCodes.length === 0) {
          setErrorState(
            'No se encontraron códigos IAE asociados a este código CNAE 2009.'
          )
          setIsLoadingState(false)
          return
        }

        associatedCodesList = associatedCodes

        // Get details for each associated IAE code
        for (const iaeCode of associatedCodesList) {
          const { data: iaeDetails, error: iaeError } = await supabase.rpc(
            'get_iae_overview_by_code',
            { iae_code: iaeCode }
          )

          if (iaeError) throw iaeError
          if (iaeDetails && iaeDetails.length > 0) {
            associatedCodesDetails.push({
              code: iaeDetails[0].cod_iae,
              title: iaeDetails[0].titulo_iae,
              description: iaeDetails[0].descripcion_iae,
              category: iaeDetails[0].categoria_iae,
              level: iaeDetails[0].nivel_iae,
              slug: iaeDetails[0].slug_iae,
            })
          }
        }
      }

      // If onConversion callback is provided, call it with the associated codes details
      if (onConversion) {
        onConversion(
          associatedCodesDetails,
          selectedCode.type,
          selectedCode.code
        )
      } else {
        // If no callback is provided, navigate to the details page
        if (selectedCode.type === 'cnae2009') {
          navigate(
            `/cnae-2009/${selectedCode.code.replace('.', '')}/${
              selectedCode.slug
            }`
          )
        } else if (selectedCode.type === 'cnae') {
          navigate(
            `/cnae/${selectedCode.code.replace('.', '')}/${selectedCode.slug}`
          )
        } else if (selectedCode.type === 'iae') {
          navigate(
            `/iae/${selectedCode.code.replace('.', '')}/${selectedCode.slug}`
          )
        } else {
          navigate(`/${selectedCode.type}/${selectedCode.slug}`)
        }
      }
    } catch (error) {
      console.error('Error converting code:', error)
      setErrorState(
        'Error al convertir el código. Por favor, inténtalo de nuevo.'
      )
    } finally {
      setIsLoadingState(false)
    }
  }

  const handleFindCorrespondence = async () => {
    if (!selectedCode) return

    try {
      setIsLoadingState(true)
      setErrorState(null)

      let correspondingCodesList = []
      let correspondingCodesDetails = []

      // Determine which function to call based on the selected code type

      if (selectedCode.type === 'cnae2009') {
        // Get CNAE 2025 codes corresponding to this CNAE 2009 code

        const { data, error: correspondingError } = await supabase.rpc(
          'get_cnae_2025_corresponding_to_cnae_2009_by_code',
          {
            cnae_code: selectedCode.code,
          }
        )

        if (correspondingError) {
          console.error(
            'Error getting corresponding codes:',
            correspondingError
          )
          throw correspondingError
        }

        if (data) {
          // The function now returns an array directly
          correspondingCodesList = data
        }

        if (!correspondingCodesList || correspondingCodesList.length === 0) {
          setErrorState(
            'No se encontraron códigos CNAE 2025 correspondientes a este código CNAE 2009.'
          )
          setIsLoadingState(false)
          return
        }

        // Get details for each corresponding CNAE 2025 code
        for (const cnaeCode of correspondingCodesList) {
          const { data: cnaeDetails, error: cnaeError } = await supabase.rpc(
            'get_cnae_2025_overview_by_code',
            { cnae_code: cnaeCode }
          )

          if (cnaeError) {
            console.error('Error getting CNAE details:', cnaeError)
            throw cnaeError
          }

          if (!cnaeDetails || cnaeDetails.length === 0) {
            continue
          }

          correspondingCodesDetails.push({
            code: cnaeDetails[0].cod_cnae,
            title: cnaeDetails[0].titulo_cnae,
            description: cnaeDetails[0].descripcion_cnae,
            category: cnaeDetails[0].categoria_cnae,
            level: cnaeDetails[0].nivel_cnae,
            slug: cnaeDetails[0].slug_cnae,
          })
        }
      } else if (selectedCode.type === 'cnae') {
        // Get CNAE 2009 codes corresponding to this CNAE 2025 code

        const { data, error: correspondingError } = await supabase.rpc(
          'get_cnae_2009_corresponding_to_cnae_2025_by_code',
          {
            cnae_code: selectedCode.code,
          }
        )

        if (correspondingError) {
          console.error(
            'Error getting corresponding codes:',
            correspondingError
          )
          throw correspondingError
        }

        if (data) {
          // The function now returns an array directly
          correspondingCodesList = data
        }

        if (!correspondingCodesList || correspondingCodesList.length === 0) {
          setErrorState(
            'No se encontraron códigos CNAE 2009 correspondientes a este código CNAE 2025.'
          )
          setIsLoadingState(false)
          return
        }

        // Get details for each corresponding CNAE 2009 code
        for (const cnaeCode of correspondingCodesList) {
          try {
            const { data: cnaeDetails, error: cnaeError } = await supabase.rpc(
              'get_cnae_2009_overview_by_code',
              { cnae_code: cnaeCode }
            )

            if (cnaeError) {
              console.error('Error getting CNAE details:', cnaeError)
              // Continue to next code instead of throwing error
              continue
            }

            if (!cnaeDetails || cnaeDetails.length === 0) {
              continue
            }

            correspondingCodesDetails.push({
              code: cnaeDetails[0].cod_cnae_2009,
              title: cnaeDetails[0].titulo_cnae_2009,
              description: cnaeDetails[0].descripcion_cnae_2009,
              category: cnaeDetails[0].categoria_cnae_2009,
              level: cnaeDetails[0].nivel_cnae_2009,
              slug: cnaeDetails[0].slug_cnae_2009,
            })
          } catch (error) {
            console.error('Exception getting CNAE details:', error)
            // Add a fallback entry with basic information
            correspondingCodesDetails.push({
              code: cnaeCode,
              title: `Código CNAE 2009: ${cnaeCode}`,
              description: 'Información detallada no disponible',
              category: '',
              level: '',
              slug: cnaeCode,
            })
          }
        }
      }

      // Call the onFindCorrespondence callback with the results
      if (onFindCorrespondence) {
        onFindCorrespondence(
          correspondingCodesDetails,
          selectedCode.type,
          selectedCode.code
        )
      }
    } catch (error) {
      console.error('Error finding correspondence:', error)
      setErrorState(
        'Error al buscar correspondencias. Por favor, inténtalo de nuevo.'
      )
    } finally {
      setIsLoadingState(false)
    }
  }

  return (
    <div className='search-container'>
      {/* SearchAction Schema */}
      <SchemaMarkup
        type='SearchAction'
        data={{
          url: 'https://conversoriaecnae.es',
        }}
      />
      <div className='search-bar' ref={searchRef}>
        <div className='search-input-container'>
          <FaSearch className='search-icon' />
          <input
            type='text'
            className='search-input'
            placeholder={
              type === 'iae'
                ? 'Buscar epígrafe IAE...'
                : type === 'cnae'
                  ? 'Buscar código CNAE 2025...'
                  : type === 'cnae2009'
                    ? 'Buscar código CNAE 2009...'
                    : type === 'both-cnae'
                      ? 'Buscar código CNAE 2025 o CNAE 2009...'
                      : 'Buscar epígrafe IAE o código CNAE 2025...'
            }
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() =>
              searchTerm.trim() &&
              searchTerm.trim().length >= 2 &&
              setShowResults(true)
            }
          />
          {selectedCode && (
            <FaTimes
              className='clear-search-icon'
              onClick={handleClearSearch}
              title='Borrar búsqueda'
            />
          )}
        </div>

        {isLoading && (
          <div className='loading'>
            <div className='loading-spinner'></div>
          </div>
        )}

        {errorState && <div className='error-message'>{errorState}</div>}

        {showResults && searchResults.length > 0 && (
          <div className='search-results'>
            {searchResults.map((result, index) => (
              <div
                key={`${result.type}-${result.code}-${index}`}
                className='search-result-item'
                onClick={() => handleCodeSelect(result)}
              >
                <div className='search-result-mobile'>
                  <span className={`code-type-badge ${result.type}`}>
                    {result.type === 'iae'
                      ? 'IAE'
                      : result.type === 'cnae'
                        ? 'CNAE'
                        : 'CNAE 2009'}
                  </span>
                  <span className={`search-result-code ${result.type}-code`}>
                    {result.code}
                  </span>
                  <span className='search-result-title'>{result.title}</span>
                </div>
                <div className='search-result-desktop'>
                  <div className='search-result-content'>
                    <span className={`search-result-code ${result.type}-code`}>
                      {result.code}
                    </span>
                    <span className='search-result-title'>{result.title}</span>
                  </div>
                  <span className={`code-type-badge ${result.type}`}>
                    {result.type === 'iae'
                      ? 'IAE'
                      : result.type === 'cnae'
                        ? 'CNAE'
                        : 'CNAE 2009'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='button-container'>
        {selectedCode && !hideConvertButton && (
          <button className='convert-btn' onClick={handleConvert}>
            Convertir{' '}
            {selectedCode.type === 'iae'
              ? 'IAE a CNAE'
              : selectedCode.type === 'cnae2009'
                ? 'CNAE 2009 a IAE'
                : 'CNAE a IAE'}
          </button>
        )}

        {selectedCode &&
          !hideCorrespondenceButton &&
          (type === 'both-cnae' || type === 'cnae' || type === 'cnae2009') && (
            <button
              className='correspondence-btn'
              onClick={handleFindCorrespondence}
            >
              Encontrar correspondencia
            </button>
          )}
      </div>
    </div>
  )
}

export default SearchBar
